import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, User, Trash2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PrivateCustomer = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    notes: ''
  });
  const [customers, setCustomers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/private-customers`);
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/private-customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({
          title: 'הצלחה!',
          description: 'לקוח פרטי נוסף בהצלחה'
        });
        setFormData({
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          address: '',
          notes: ''
        });
        loadCustomers();
      }
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'אירעה שגיאה בשמירת הנתונים',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק לקוח זה?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/private-customers/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: 'הצלחה',
          description: 'לקוח נמחק בהצלחה'
        });
        loadCustomers();
      }
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'אירעה שגיאה במחיקת הלקוח',
        variant: 'destructive'
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-6xl">
        <Card className="shadow-2xl border-0 mb-6">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-t-lg">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <User className="w-8 h-8" />
                <CardTitle className="text-3xl font-bold">לקוח פרטי</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-base font-semibold text-gray-700">
                    שם פרטי
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="הזן שם פרטי"
                    className="text-base py-5 border-2"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-base font-semibold text-gray-700">
                    שם משפחה
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="הזן שם משפחה"
                    className="text-base py-5 border-2"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-base font-semibold text-gray-700">
                    טלפון
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="הזן מספר טלפון"
                    className="text-base py-5 border-2"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-base font-semibold text-gray-700">
                    אימייל
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="הזן כתובת אימייל"
                    className="text-base py-5 border-2"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="text-base font-semibold text-gray-700">
                    כתובת
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="הזן כתובת"
                    className="text-base py-5 border-2"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes" className="text-base font-semibold text-gray-700">
                  הערות
                </Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="הזן הערות"
                  className="min-h-[100px] text-base border-2"
                />
              </div>

              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-6 text-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  {isSubmitting ? 'שומר...' : 'הוסף לקוח'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/customers-list')}
                  className="px-8 py-6 text-lg border-2"
                >
                  <ArrowRight className="ml-2 w-5 h-5" />
                  חזור
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Customers List */}
        {customers.length > 0 && (
          <Card className="shadow-2xl border-0">
            <CardHeader className="bg-gradient-to-r from-pink-600 to-purple-600 text-white">
              <CardTitle className="text-2xl font-bold">רשימת לקוחות פרטיים</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-right font-semibold">שם פרטי</th>
                      <th className="p-3 text-right font-semibold">שם משפחה</th>
                      <th className="p-3 text-right font-semibold">טלפון</th>
                      <th className="p-3 text-right font-semibold">אימייל</th>
                      <th className="p-3 text-center font-semibold">פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => (
                      <tr key={customer.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">{customer.firstName}</td>
                        <td className="p-3">{customer.lastName}</td>
                        <td className="p-3">{customer.phone}</td>
                        <td className="p-3">{customer.email || '-'}</td>
                        <td className="p-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(customer.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PrivateCustomer;
